import { Component, Input, signal, output, inject, effect, input, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { PinchZoomComponent } from '@meddv/ngx-pinch-zoom';
import { CommonModule } from '@angular/common';
import { ClarityModule } from '@clr/angular';
import { FileForPreview } from '../preview-thumbnails/preview-thumbnail.type';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { Base64Service } from 'src/app/services/base64/base64.service';

@Component({
  selector: 'app-mediaviewmodal',
  templateUrl: './mediaviewmodal.component.html',
  styleUrls: ['./mediaviewmodal.component.scss'],
  imports: [CommonModule, ClarityModule, PinchZoomComponent, MrTranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewModalComponent {
  private overlayService = inject(OverlayService);
  private base64service = inject(Base64Service);

  /**
   * @alias getBigFileFnc
   * @function base64 beim Server anfordern (mit Dateigröße ab 9,5 MB)
   * @async
   * @returns Promise - string
   */
  readonly fileBase64FromServer = input<(idOrName: number | string) => Promise<string>>(undefined, {
    alias: 'getBigFileFnc',
  });
  /**
   * @function base64[] beim Server anfordern (mit Dateigröße bis 9,5 MB)
   * @async
   * @returns Promise - {base64, name, id?}[]
   */
  readonly getMediaFromServerAll = input<(fileNames: string[], subfolder: string) => Promise<Array<object>>>(undefined);
  readonly openPdfOverlay = output<File>();

  public readonly deleteFoto = output<any>();

  /**
   * @description use to show files locally from base64
   */
  @Input() set media(view: FileForMediaView) {
    this.previewThumbnails.set([]);
    this.currentThumb.set(undefined);
    if (view?.files.length && !this.loading()) {
      this.loading.set(true);
      setTimeout(() => {
        if (view.deleteable) {
          this.isDeleteAllowed.set(true);
        }

        if (view.isSkizze || view.title == 'Messpunktskizze') {
          this.isSkizze.set(true);
        }

        this.unpackFiles(view.title, view.files);

        if (this.previewThumbnails().length) {
          const index = (view.currentIndex ?? view.currentFile) ? view.files.indexOf(view.currentFile) : 0;
          this.changeIndexTo(index == -1 ? 0 : index);
          this.modalOpen.set(true);
        }
        this.loading.set(false);
      });
    }
  }
  /**
   * @description use to load files from back-end by names
   */
  @Input() set srcs(files: TitleAndFileNames[]) {
    this.previewThumbnails.set([]);
    this.currentThumb.set(undefined);
    if (files?.length && !this.loading()) {
      this.loading.set(true);
      setTimeout(() => {
        this.getFiles(files);
      });
    }
  }
  private readonly loading = signal<boolean>(undefined);

  readonly onClose = output<void>();
  readonly modalOpen = signal<boolean>(undefined);
  constructor() {
    effect(() => {
      if (this.modalOpen() === false) this.onClose.emit();
    });
  }
  protected readonly isDeleteAllowed = signal(false);
  protected readonly isSkizze = signal(false);
  protected readonly currentIndex = signal(0);
  protected readonly currentThumb = signal<any>(undefined);
  protected readonly previewThumbnails = signal<any[]>([]);
  protected readonly loadingFile = signal(false);

  private readonly domSanitizer = inject(DomSanitizer);
  private readonly toastr = inject(ToastrService);
  // private readonly mrTranslate = inject(MrTranslatePipe);

  private async getFiles(fileNames: TitleAndFileNames[]) {
    const promisesArray = fileNames.map(
      ({ title = 'Gallerie', subfolder, fileNames }) =>
        new Promise<{ title: string; files: any[] }>(async (resolve, reject) => {
          try {
            const files = await this.getMediaFromServerAll()(fileNames, subfolder);
            resolve({
              title: title,
              files: files,
            });
          } catch (er) {
            reject(new Error(title + ': ' + (er as Error).message));
          }
        })
    );

    (await Promise.allSettled(promisesArray)).forEach(
      ({ status, value: { title, files } = { title: '', files: [] }, reason }: any) => {
        if (status == 'fulfilled') this.unpackFiles(title, files);
        else this.toastr.error((reason as Error).message);
      }
    );

    if (this.previewThumbnails().length) {
      this.changeIndexTo(0);
      this.modalOpen.set(true);
    } else this.onClose.emit();
    this.loading.set(false);
  }

  private unpackFiles(title: string, files: any) {
    files.forEach(({ extension = null, name = '.png', base64, id = undefined, url = null }: any) => {
      extension ??= name?.split('.').pop().toLowerCase();
      let plays = false;
      let src = '';
      switch (extension) {
        case 'svg':
          src = 'data:image/' + extension + '+xml;base64,' + base64;
          base64 = null;
          break;
        case 'webm':
        case 'mp4':
        case 'mov':
          src = '/assets/icons/videoicon.png';
          plays = true;
          break;
        case 'pdf':
          src = '/assets/icons/pdf-file-icon.png';
          plays = false;
          break;
        default:
          src = 'data:image/' + extension + ';base64,' + base64;
          base64 = null;
          break;
      }
      this.previewThumbnails().push({
        src: this.domSanitizer.bypassSecurityTrustUrl(src),
        title: title,
        name: name,
        id: id,
        url,
        base64: base64,
        plays: plays,
      });
    });
  }

  private async openVideo(index: number = undefined) {
    const fileObj = index ? this.previewThumbnails()[index] : this.currentThumb();
    if (!fileObj.url) {
      this.loadingFile.set(true);
      try {
        //! base64
        //   - fileObj
        //   - beim Server anfordern, wenn Size mehr als 9,5 MB
        const base64 = fileObj.base64 || (await this.fileBase64FromServer()(fileObj.id || fileObj.name));
        const fileUrl = 'data:video/mp4;base64,' + base64;
        fileObj.url = fileUrl;
      } catch (er) {
        this.toastr.error((er as Error).message);
      }
      this.loadingFile.set(false);
    }
  }

  protected deleteItem() {
    let index = this.currentIndex();
    const thumb = this.previewThumbnails()[index];

    this.previewThumbnails.update(thumbs => (thumbs.splice(index, 1), [...thumbs]));

    this.deleteFoto.emit(index);

    if (this.previewThumbnails().length > 0) {
      this.moveGallery('back');
    } else {
      this.modalOpen.set(false);
      this.onClose.emit();
    }
  }

  protected moveGallery(mode: string) {
    let nextIndex = this.currentIndex();
    if (mode == 'right') nextIndex++;
    else if (mode == 'left') nextIndex--;

    if (nextIndex >= this.previewThumbnails().length) nextIndex = 0;
    if (nextIndex < 0) nextIndex = this.previewThumbnails().length - 1;

    this.changeIndexTo(nextIndex);
  }

  protected changeIndexTo(index: number) {
    this.currentIndex.set(index);
    this.currentThumb.set(this.previewThumbnails()[index]);

    if (this.currentThumb().plays) this.openVideo();
  }
  /**
   * @method Konvertiert base64 zu File, if kommt vom Server
   * @emits openPdfOverlay(File)
   */
  protected async openPdfDoc(index: number) {
    const fileObj = this.previewThumbnails()[index];
    try {
      //! File
      //* - wenn neu hochgeladen;
      if (!(fileObj.url instanceof File)) {
        //* - aus base64:
        //   - fileObj
        //   - beim Server anfordern, wenn Size mehr als 9,5 MB
        fileObj.base64 ??= await this.fileBase64FromServer()(fileObj.name);
        fileObj.file = this.base64service.convertFileAsBlob(fileObj.base64, 'application/pdf', fileObj.name);
      }
      if (!(fileObj.file instanceof File)) throw new Error('Could not convert to File');
      this.openPdfOverlay.emit(fileObj.file);
    } catch (er) {
      this.toastr.error((er as Error).message);
    }
  }
}

export interface TitleAndFileNames {
  title?: string;
  subfolder?: string;
  fileNames: string[];
}

export interface FileForMediaView {
  title?: string;
  deleteable?: boolean;
  currentFile?: FileForPreview;
  currentIndex?: number;
  isSkizze?: boolean;
  files: FileForPreview[];
}
